import React, { useState, useEffect, useRef } from 'react';
import './OrderTrackingModal.css';
import baseURL from '../url';
import moneda from '../moneda';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClipboardCheck,
    faUtensils,
    faMotorcycle,
    faCheckCircle,
    faConciergeBell,
    faTimes,
    faCopy,
    faCheck,
    faSync,
    faMapMarkerAlt,
    faStore,
    faUser,
    faReceipt,
    faPhone,
    faClock,
    faBan
} from '@fortawesome/free-solid-svg-icons';
import { extraerFechaLima, formatearHoraLima, formatearFechaCortaLima } from '../../utils/dateHelper';

export default function OrderTrackingModal() {
    const [isOpen, setIsOpen] = useState(false);
    const [idComanda, setIdComanda] = useState(null);
    const [comanda, setComanda] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [copiado, setCopiado] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Revisar query param en la URL (?pedido=ID o ?tracking=ID o ?comanda=ID)
    const checkUrlParams = () => {
        try {
            const params = new URLSearchParams(window.location.search);
            const idParam = params.get('pedido') || params.get('tracking') || params.get('comanda');
            if (idParam && !isNaN(idParam)) {
                setIdComanda(parseInt(idParam));
                setIsOpen(true);
            }
        } catch (e) {
            console.error('Error al leer URL params para tracking:', e);
        }
    };

    // Escuchar eventos de apertura manual (por ejemplo al finalizar la compra en Cart)
    useEffect(() => {
        checkUrlParams();

        const handleAbrirTracking = (e) => {
            const nuevoId = e.detail?.idComanda;
            if (nuevoId) {
                setIdComanda(parseInt(nuevoId));
                setIsOpen(true);
            }
        };

        const handlePopState = () => {
            checkUrlParams();
        };

        window.addEventListener('abrir-tracking', handleAbrirTracking);
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('abrir-tracking', handleAbrirTracking);
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    // Cargar datos de la comanda
    const cargarComanda = async (showLoading = true) => {
        if (!idComanda) return;
        if (showLoading) setLoading(true);
        setIsRefreshing(true);
        setErrorMsg('');

        try {
            const res = await fetch(`${baseURL}/comandasGet.php?idComanda=${idComanda}`);
            const data = await res.json();
            if (data.status === 'success' && data.comanda) {
                setComanda(data.comanda);
            } else if (data.comandas && data.comandas.length > 0) {
                setComanda(data.comandas[0]);
            } else {
                setErrorMsg(`No se encontró el pedido #${idComanda}. Verifica que el número sea correcto.`);
            }
        } catch (err) {
            console.error('Error al cargar comanda tracking:', err);
            setErrorMsg('Hubo un problema de conexión al consultar el pedido. Intenta nuevamente.');
        } finally {
            if (showLoading) setLoading(false);
            setTimeout(() => setIsRefreshing(false), 400);
        }
    };

    useEffect(() => {
        if (isOpen && idComanda) {
            cargarComanda(true);
        }
    }, [isOpen, idComanda]);

    // Polling en tiempo real cada 6 segundos mientras el modal esté abierto
    useEffect(() => {
        if (!isOpen || !idComanda) return;

        const interval = setInterval(() => {
            cargarComanda(false);
        }, 6000);

        return () => clearInterval(interval);
    }, [isOpen, idComanda]);

    // Cerrar modal y limpiar parámetro de la URL sin recargar la página
    const handleClose = () => {
        setIsOpen(false);
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete('pedido');
            url.searchParams.delete('tracking');
            url.searchParams.delete('comanda');
            window.history.replaceState({}, '', url.pathname + (url.search ? url.search : ''));
        } catch (e) {
            console.error('Error al limpiar URL params:', e);
        }
    };

    // Copiar enlace de seguimiento
    const handleCopiarEnlace = () => {
        if (!idComanda) return;
        const currentUrl = `${window.location.origin}${window.location.pathname}?pedido=${idComanda}`;
        if (navigator.clipboard) {
            navigator.clipboard.writeText(currentUrl).then(() => {
                setCopiado(true);
                setTimeout(() => setCopiado(false), 2500);
            });
        }
    };

    if (!isOpen) return null;

    // Calcular estado y paso activo
    const estado = comanda?.estado || 'pendiente';
    const esMesa = comanda?.tipo_pedido === 'mesa';
    const esCancelado = estado === 'cancelado';

    let currentStep = 1;
    if (estado === 'pendiente') currentStep = 1;
    else if (estado === 'en_preparacion') currentStep = 2;
    else if (estado === 'en_camino' || estado === 'servido') currentStep = 3;
    else if (estado === 'entregado' || estado === 'pagado') currentStep = 4;

    const progresoPorcentaje = esCancelado
        ? 0
        : currentStep === 1
            ? 15
            : currentStep === 2
                ? 50
                : currentStep === 3
                    ? 80
                    : 100;

    const items = comanda?.items || [];
    const telefonoTienda = comanda?.telefono_tienda || '';
    const fechaLima = comanda?.fecha_registro ? extraerFechaLima(comanda.fecha_registro) : '';
    const horaLima = comanda?.fecha_registro ? formatearHoraLima(comanda.fecha_registro) : '';
    const fechaCorta = fechaLima ? formatearFechaCortaLima(fechaLima) : '';

    return (
        <div className="tracking-modal-overlay" onClick={handleClose}>
            <div className="tracking-modal-dialog" onClick={(e) => e.stopPropagation()}>
                {/* ENCABEZADO */}
                <div className="tracking-modal-header">
                    <div className="tracking-header-left">
                        <div className="tracking-header-icon-box">
                            <FontAwesomeIcon icon={faReceipt} />
                        </div>
                        <div className="tracking-header-titles">
                            <div className="tracking-order-badge">
                                <span>Pedido #{idComanda}</span>
                                {comanda?.nombre_tienda && (
                                    <span className="tracking-store-tag">
                                        <FontAwesomeIcon icon={faStore} style={{ marginRight: '4px' }} />
                                        {comanda.nombre_tienda}
                                    </span>
                                )}
                            </div>
                            <h3>Estado de tu Pedido</h3>
                        </div>
                    </div>

                    <div className="tracking-header-actions">
                        <button
                            type="button"
                            className={`tracking-btn-refresh ${isRefreshing ? 'spinning' : ''}`}
                            onClick={() => cargarComanda(false)}
                            title="Actualizar estado ahora"
                        >
                            <FontAwesomeIcon icon={faSync} />
                        </button>
                        <button
                            type="button"
                            className="tracking-btn-close"
                            onClick={handleClose}
                            title="Cerrar ventana"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>

                {/* CONTENIDO PRINCIPAL */}
                <div className="tracking-modal-body">
                    {loading && !comanda ? (
                        <div className="tracking-loading-state">
                            <div className="tracking-spinner"></div>
                            <p>Consultando el estado de tu pedido en tiempo real...</p>
                        </div>
                    ) : errorMsg ? (
                        <div className="tracking-error-state">
                            <FontAwesomeIcon icon={faBan} className="tracking-error-icon" />
                            <h4>No pudimos cargar el pedido</h4>
                            <p>{errorMsg}</p>
                            <button
                                type="button"
                                className="tracking-btn-retry"
                                onClick={() => cargarComanda(true)}
                            >
                                <FontAwesomeIcon icon={faSync} /> Reintentar
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* BANNER DINÁMICO DE ESTADO */}
                            <div className={`tracking-hero-status ${esCancelado ? 'cancelado' : `step-${currentStep}`}`}>
                                <div className="tracking-hero-icon-bubble">
                                    {esCancelado ? (
                                        <FontAwesomeIcon icon={faBan} />
                                    ) : currentStep === 1 ? (
                                        <FontAwesomeIcon icon={faClipboardCheck} />
                                    ) : currentStep === 2 ? (
                                        <FontAwesomeIcon icon={faUtensils} />
                                    ) : currentStep === 3 ? (
                                        esMesa ? <FontAwesomeIcon icon={faConciergeBell} /> : <FontAwesomeIcon icon={faMotorcycle} />
                                    ) : (
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                    )}
                                </div>
                                <div className="tracking-hero-text">
                                    {esCancelado ? (
                                        <>
                                            <h4>Pedido Cancelado</h4>
                                            <p>Este pedido ha sido cancelado. Si tienes dudas, contáctanos directamente.</p>
                                        </>
                                    ) : currentStep === 1 ? (
                                        <>
                                            <h4>¡Hemos recibido tu pedido!</h4>
                                            <p>Tu orden fue registrada correctamente y está en cola para preparación.</p>
                                        </>
                                    ) : currentStep === 2 ? (
                                        <>
                                            <h4>¡Tu pedido está en Cocina!</h4>
                                            <p>El equipo está preparando tus platos con esmero y rapidez.</p>
                                        </>
                                    ) : currentStep === 3 ? (
                                        esMesa ? (
                                            <>
                                                <h4>¡Tu orden va a la mesa!</h4>
                                                <p>El mozo se dirige a la mesa con tus productos recién salidos de cocina.</p>
                                            </>
                                        ) : (
                                            <>
                                                <h4>¡Tu pedido va en camino!</h4>
                                                <p>El repartidor ya salió rumbo a tu dirección de entrega.</p>
                                            </>
                                        )
                                    ) : (
                                        <>
                                            <h4>¡Pedido Entregado con Éxito!</h4>
                                            <p>¡Muchas gracias por tu compra! Esperamos que lo disfrutes.</p>
                                        </>
                                    )}
                                </div>
                                <div className="tracking-hero-live-badge">
                                    <span className="tracking-pulse-dot"></span>
                                    <span>En vivo •</span>
                                </div>
                            </div>

                            {/* BARRA DE PROGRESO STEPPER */}
                            {!esCancelado && (
                                <div className="tracking-stepper-container">
                                    <div className="tracking-stepper-progress-bg">
                                        <div
                                            className="tracking-stepper-progress-fill"
                                            style={{ width: `${progresoPorcentaje}%` }}
                                        />
                                    </div>

                                    <div className="tracking-stepper-steps">
                                        {/* PASO 1: REGISTRADO */}
                                        <div className={`tracking-step-item ${currentStep >= 1 ? 'completed' : ''} ${currentStep === 1 ? 'active' : ''}`}>
                                            <div className="tracking-step-circle">
                                                {currentStep > 1 ? (
                                                    <FontAwesomeIcon icon={faCheck} />
                                                ) : (
                                                    <FontAwesomeIcon icon={faClipboardCheck} />
                                                )}
                                            </div>
                                            <div className="tracking-step-labels">
                                                <span className="step-title">Pedido</span>
                                                <span className="step-sub">Registrado</span>
                                            </div>
                                        </div>

                                        {/* PASO 2: COCINA */}
                                        <div className={`tracking-step-item ${currentStep >= 2 ? 'completed' : ''} ${currentStep === 2 ? 'active' : ''}`}>
                                            <div className="tracking-step-circle">
                                                {currentStep > 2 ? (
                                                    <FontAwesomeIcon icon={faCheck} />
                                                ) : (
                                                    <FontAwesomeIcon icon={faUtensils} />
                                                )}
                                            </div>
                                            <div className="tracking-step-labels">
                                                <span className="step-title">Cocina</span>
                                                <span className="step-sub">En preparación</span>
                                            </div>
                                        </div>

                                        {/* PASO 3: EN CAMINO / EN MESA */}
                                        <div className={`tracking-step-item ${currentStep >= 3 ? 'completed' : ''} ${currentStep === 3 ? 'active' : ''}`}>
                                            <div className="tracking-step-circle">
                                                {currentStep > 3 ? (
                                                    <FontAwesomeIcon icon={faCheck} />
                                                ) : esMesa ? (
                                                    <FontAwesomeIcon icon={faConciergeBell} />
                                                ) : (
                                                    <FontAwesomeIcon icon={faMotorcycle} />
                                                )}
                                            </div>
                                            <div className="tracking-step-labels">
                                                <span className="step-title">{esMesa ? 'A la Mesa' : 'En Camino'}</span>
                                                <span className="step-sub">{esMesa ? 'Listo para servir' : 'Reparto en ruta'}</span>
                                            </div>
                                        </div>

                                        {/* PASO 4: ENTREGADO */}
                                        <div className={`tracking-step-item ${currentStep >= 4 ? 'completed' : ''} ${currentStep === 4 ? 'active' : ''}`}>
                                            <div className="tracking-step-circle">
                                                <FontAwesomeIcon icon={faCheckCircle} />
                                            </div>
                                            <div className="tracking-step-labels">
                                                <span className="step-title">Entregado</span>
                                                <span className="step-sub">¡Buen provecho!</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* TARJETA DE RESUMEN DEL PEDIDO */}
                            <div className="tracking-details-card">
                                <div className="tracking-details-header">
                                    <div className="tracking-info-meta">
                                        {fechaCorta && (
                                            <span>
                                                <FontAwesomeIcon icon={faClock} style={{ marginRight: '5px', color: '#64748b' }} />
                                                {fechaCorta} • {horaLima}
                                            </span>
                                        )}
                                        <span className="tracking-dot-divider">•</span>
                                        <span className="tracking-pill-tipo">
                                            {esMesa
                                                ? `🍽️ Mesa: ${comanda?.numero_mesa || 'Local'}`
                                                : comanda?.tipo_pedido === 'retiro'
                                                    ? '🏪 Retiro en Tienda'
                                                    : '🛵 Envío a Domicilio'}
                                        </span>
                                    </div>
                                </div>

                                {/* LISTA DE PRODUCTOS */}
                                <div className="tracking-items-list">
                                    {items.map((item, idx) => (
                                        <div key={idx} className="tracking-item-row">
                                            <div className="tracking-item-left">
                                                <span className="tracking-item-qty">{item.cantidad}x</span>
                                                <div className="tracking-item-texts">
                                                    <strong className="tracking-item-title">{item.titulo}</strong>
                                                    {item.item && (
                                                        <span className="tracking-item-variant">
                                                            {Array.isArray(item.item) ? item.item.filter(Boolean).join(', ') : item.item}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="tracking-item-price">
                                                {comanda?.moneda || moneda} {(Number(item.precio) * Number(item.cantidad)).toFixed(2)}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* INFORMACIÓN DE ENTREGA / CLIENTE */}
                                <div className="tracking-extra-info">
                                    {comanda?.cliente_nombre && (
                                        <div className="tracking-info-line">
                                            <FontAwesomeIcon icon={faUser} className="info-icon" />
                                            <div>
                                                <label>Cliente:</label>
                                                <span>{comanda.cliente_nombre}</span>
                                            </div>
                                        </div>
                                    )}

                                    {!esMesa && comanda?.cliente_direccion && (
                                        <div className="tracking-info-line">
                                            <FontAwesomeIcon icon={faMapMarkerAlt} className="info-icon" />
                                            <div>
                                                <label>Dirección de entrega:</label>
                                                <span>{comanda.cliente_direccion}</span>
                                            </div>
                                        </div>
                                    )}

                                    {comanda?.nota && (
                                        <div className="tracking-info-line">
                                            <span className="info-icon">📝</span>
                                            <div>
                                                <label>Indicaciones:</label>
                                                <span>{comanda.nota}</span>
                                            </div>
                                        </div>
                                    )}

                                    {comanda?.forma_pago && (
                                        <div className="tracking-info-line">
                                            <span className="info-icon">💳</span>
                                            <div>
                                                <label>Forma de pago:</label>
                                                <span style={{ textTransform: 'capitalize' }}>
                                                    {comanda.forma_pago === 'efectivo'
                                                        ? 'Pago en efectivo'
                                                        : comanda.forma_pago === 'transferencia'
                                                            ? 'Transferencia bancaria / Yape'
                                                            : comanda.forma_pago}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* TOTALES */}
                                <div className="tracking-totals-box">
                                    {Number(comanda?.descuento) > 0 && (
                                        <div className="tracking-subtotal-line discount">
                                            <span>Cupón de descuento:</span>
                                            <span>- {comanda?.moneda || moneda} {Number(comanda.descuento).toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="tracking-total-line">
                                        <span>Total a pagar:</span>
                                        <strong>{comanda?.moneda || moneda} {Number(comanda?.total || 0).toFixed(2)}</strong>
                                    </div>
                                </div>
                            </div>

                            {/* BARRA DE ACCIONES / ENLACE DE SEGUIMIENTO */}
                            <div className="tracking-actions-bar">
                                <div className="tracking-link-share-box">
                                    <span className="tracking-link-label">Link de rastreo para este pedido:</span>
                                    <div className="tracking-link-input-group">
                                        <input
                                            type="text"
                                            readOnly
                                            value={`${window.location.origin}${window.location.pathname}?pedido=${idComanda}`}
                                            className="tracking-link-input"
                                            onClick={(e) => e.target.select()}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleCopiarEnlace}
                                            className={`tracking-btn-copy ${copiado ? 'copied' : ''}`}
                                            title="Copiar enlace de seguimiento"
                                        >
                                            <FontAwesomeIcon icon={copiado ? faCheck : faCopy} />
                                            <span>{copiado ? '¡Copiado!' : 'Copiar'}</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="tracking-modal-footer-btns">
                                    {telefonoTienda && (
                                        <a
                                            href={`https://api.whatsapp.com/send?phone=${telefonoTienda}&text=${encodeURIComponent(`Hola, tengo una consulta sobre mi pedido #${idComanda}`)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="tracking-btn-wpp"
                                        >
                                            <FontAwesomeIcon icon={faPhone} />
                                            <span>Contactar a la tienda</span>
                                        </a>
                                    )}
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        className="tracking-btn-done"
                                    >
                                        Seguir navegando en la carta
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
